import { $isRootTextContentEmpty } from "@lexical/text";
import { EditorState } from "lexical";

import { isStringValidRegalDateFormat } from "../../../../services/TimeService/TimeService";
import { DEFAULT_HANDLEBARS, DEFAULT_HANDLEBARS_END } from "./constants";

import type { ConfigurableVariableTriggerOption } from "../plugins/types";

export function isContentEmpty(state?: EditorState) {
    return (
        state?.read?.(() => {
            return $isRootTextContentEmpty(false);
        }) ?? true
    );
}

export function validateRequiredLexicalField(message: string) {
    return {
        validator(_: any, state?: EditorState) {
            if (isContentEmpty(state)) {
                return Promise.reject(message);
            }

            return Promise.resolve();
        },
    };
}

const triggerValidator = (triggers: ConfigurableVariableTriggerOption, value: string) => {
    let hasTriggers = false;

    triggers.forEach((trigger) => {
        if (value.includes(trigger.openingTag) && value.includes(trigger.closingTag)) {
            hasTriggers = true;
        }
    });
    return hasTriggers;
};

export function generateLexicalDateTimeValidator(message: string, triggers: ConfigurableVariableTriggerOption) {
    return {
        validator(_: unknown, value: string) {
            if (triggerValidator(triggers, value)) {
                return Promise.resolve();
            }

            if (isStringValidRegalDateFormat(value)) {
                return Promise.resolve();
            }
            return Promise.reject(message);
        },
    };
}

export function generateLexicalBooleanValidator(message: string, triggers: ConfigurableVariableTriggerOption) {
    return {
        validator(_: unknown, value: string) {
            if (triggerValidator(triggers, value)) {
                return Promise.resolve();
            }

            if (value.toLocaleLowerCase() === "true" || value.toLocaleLowerCase() === "false") {
                return Promise.resolve();
            }
            return Promise.reject(message);
        },
    };
}

export function generateLexicalNumberValidator(message: string, triggers: ConfigurableVariableTriggerOption) {
    return {
        validator(_: any, value: string) {
            if (triggerValidator(triggers, value)) {
                return Promise.resolve();
            }

            if (!isNaN(Number(value))) {
                return Promise.resolve();
            }
            return Promise.reject(message);
        },
    };
}

function escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function checkForWhiteSpaceWithinTags({
    text,
    openingTag = DEFAULT_HANDLEBARS,
    closingTag = DEFAULT_HANDLEBARS_END,
}: {
    text: string;
    openingTag?: string;
    closingTag?: string;
}): boolean {
    const escapedOpeningTag = escapeRegExp(openingTag);
    const escapedClosingTag = escapeRegExp(closingTag);
    // Match any whitespace between opening tag and closing tag, including within the variable name
    const regex = new RegExp(`${escapedOpeningTag}([^${escapedClosingTag}]*)${escapedClosingTag}`, "g");

    const matches = [...text.matchAll(regex)];
    const hasWhitespaceInAny = matches.some((match) => /\s/.test(match[1]));

    return hasWhitespaceInAny;
}

export const checkForStartingValuesWithinTags = ({
    values,
    text,
    openingTag = DEFAULT_HANDLEBARS,
    closingTag = DEFAULT_HANDLEBARS_END,
}: {
    values: Array<string>;
    text: string;
    openingTag?: string;
    closingTag?: string;
}) => {
    const escapedOpeningTag = escapeRegExp(openingTag);
    const escapedClosingTag = escapeRegExp(closingTag);
    // Match any text between opening tag and closing tag
    const regex = new RegExp(`${escapedOpeningTag}([^${escapedClosingTag}]*)${escapedClosingTag}`, "g");

    const matches = [...text.matchAll(regex)];
    if (matches.length === 0) {
        return true;
    }

    let startsWithEitherValue = true;

    for (const match of matches) {
        const matchValue = match[1].trim();
        // check that the matchValue starts with any of the values
        startsWithEitherValue = values.some((value) => matchValue.startsWith(value));
        if (!startsWithEitherValue) {
            break;
        }
    }

    return startsWithEitherValue;
};

/**
 * Returns true if text contains malformed variable closing (e.g. {{ ... ]] instead of {{ ... }}).
 * Only considers same-line: {{ followed by ]] without }} in between.
 */
export function checkForMalformedHandlebarsClosing({
    text,
    openingTag = DEFAULT_HANDLEBARS,
    correctClosingTag = DEFAULT_HANDLEBARS_END,
}: {
    text: string;
    openingTag?: string;
    correctClosingTag?: string;
}): boolean {
    if (typeof text !== "string" || !text.includes(openingTag)) {
        return false;
    }
    const lines = text.split("\n");
    for (const line of lines) {
        let searchFrom = 0;
        while (searchFrom < line.length) {
            const openIdx = line.indexOf(openingTag, searchFrom);
            if (openIdx === -1) {
                break;
            }
            const afterOpen = line.slice(openIdx + openingTag.length);
            const correctCloseIdx = afterOpen.indexOf(correctClosingTag);
            const wrongCloseIdx = afterOpen.indexOf("]]");
            if (wrongCloseIdx !== -1 && (correctCloseIdx === -1 || wrongCloseIdx < correctCloseIdx)) {
                return true;
            }
            searchFrom =
                correctCloseIdx !== -1
                    ? openIdx + openingTag.length + correctCloseIdx + correctClosingTag.length
                    : openIdx + openingTag.length;
        }
    }
    return false;
}
